import React from "react";
import { X } from "lucide-react";
import heartLoading from "../../../assests/heartLoading.gif";

const ContractDetailModal = ({
  selectedContractForView,
  contractDetails,
  contractLoading,
  setShowContractModal,
  onClose,
}) => {
  // Function to render frequency details based on frequency type
  const renderFrequencyDetails = () => {
    if (!contractDetails) return null;

    const frequency = contractDetails.frequency;
    const time = contractDetails.time;
    const daysOfWeek = contractDetails.daysOfWeek;
    const dayOfMonth = contractDetails.dayOfMonth;
    const selectedPeriods = contractDetails.selectedPeriods;

    // Helper to format days of week
    const formatDaysOfWeek = (days) => {
      if (!days || days === "") return "N/A";
      if (Array.isArray(days)) return days.join(", ");
      if (typeof days === "string") {
        // If it's comma separated string
        return days
          .split(",")
          .map((d) => d.trim())
          .join(", ");
      }
      return days;
    };

    // Helper to format periods
    const formatPeriods = (periods) => {
      if (!periods || periods === "") return "N/A";
      if (Array.isArray(periods)) return periods.join(", ");
      if (typeof periods === "string") {
        return periods
          .split(",")
          .map((p) => p.trim())
          .join(", ");
      }
      return periods;
    };

    const frequencyConfig = {
      Hourly: [{ label: "Frequency", value: frequency || "N/A" }],
      Daily: [
        { label: "Frequency", value: frequency || "N/A" },
        { label: "Time", value: time ? `${time} UTC` : "N/A" },
      ],
      Weekly: [
        { label: "Frequency", value: frequency || "N/A" },
        { label: "Days of Week", value: formatDaysOfWeek(daysOfWeek) },
        { label: "Time", value: time ? `${time} UTC` : "N/A" },
      ],
      "Mars Periodic": [
        { label: "Frequency", value: frequency || "N/A" },
        { label: "Day", value: dayOfMonth || "N/A" },
        { label: "Time", value: time ? `${time} UTC` : "N/A" },
      ],
      "Mars Quarter": [
        { label: "Frequency", value: frequency || "N/A" },
        { label: "Periods", value: formatPeriods(selectedPeriods) },
        { label: "Day of Period", value: dayOfMonth || "N/A" },
        { label: "Time", value: time ? `${time} UTC` : "N/A" },
      ],
      "Mars Year": [
        { label: "Frequency", value: frequency || "N/A" },
        { label: "Periods", value: formatPeriods(selectedPeriods) },
        { label: "Day of Period", value: dayOfMonth || "N/A" },
        { label: "Time", value: time ? `${time} UTC` : "N/A" },
      ],
      "Mars Half-Yearly": [
        { label: "Frequency", value: frequency || "N/A" },
        { label: "Periods", value: formatPeriods(selectedPeriods) },
        { label: "Day of Period", value: dayOfMonth || "N/A" },
        { label: "Time", value: time ? `${time} UTC` : "N/A" },
      ],
    };
    const fieldsToDisplay = frequencyConfig[frequency] || [
      { label: "Frequency", value: frequency || "N/A" },
    ];

    return (
      <div className="grid grid-cols-2 gap-4">
        {fieldsToDisplay.map((field, index) => (
          <div key={index} className="bg-gray-50 p-4 rounded-lg">
            <div className="text-sm text-gray-600 mb-1">{field.label}</div>
            <div className="text-base font-semibold text-gray-900">
              {field.value}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Function to render quality dimensions
  const renderQualityDimensions = () => {
    if (!contractDetails) return null;

    const dimensions = [
      { name: "Completeness", value: contractDetails.completeness },
      { name: "Accuracy", value: contractDetails.accuracy },
      { name: "Consistency", value: contractDetails.consistency },
      { name: "Freshness", value: contractDetails.freshness },
      { name: "Uniqueness", value: contractDetails.uniqueness },
      { name: "Validity", value: contractDetails.validity },
    ];

    return (
      <div className="grid grid-cols-6 gap-4">
        {dimensions.map((dimension, index) => (
        <div className="text-center p-4 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-500 mb-1">{dimension?.name}</p>
            <p className={`text-xl font-bold ${dimension.value !== null ? 'text-green-600' : 'text-gray-400'}`}>
              {dimension.value !== null ? `${dimension.value}%` : 'N/A'}
            </p>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
      <div className="bg-white rounded-lg shadow-xl w-4/5 max-h-[90vh] overflow-hidden">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gradient-to-r from-[#bb1ccc] to-[#0000d0]">
          <div>
            <h2 className="text-2xl font-bold text-white">
              Data Contract Details
            </h2>
            <p className="text-blue-100 text-sm mt-1">
              {selectedContractForView?.name?.replace(/\|/g, ".")}
            </p>
          </div>
          <button
            onClick={onClose || (() => setShowContractModal(false))}
            className="text-white hover:text-gray-200 transition-colors p-2 hover:bg-white/10 rounded-full"
            title="Close"
          >
            <X className="h-6 " />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-100px)]">
          {contractLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <img
                  src={heartLoading}
                  alt="Loading..."
                  style={{ width : 200}}
                />
                <p className="text-gray-600 text-lg">
                  Loading contract details...
                </p>
              </div>
            </div>
          ) : contractDetails ? (
            <div className="space-y-8">
              {/* Promised Data Quality Dimension Section */}
              <div>
                <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                  <div className="w-[5px] h-6 bg-green-600 rounded mr-3"></div>
                  Promised Data Quality Dimension
                </h3>
                {renderQualityDimensions()}
              </div>

              {/* DQ Check Frequency Section */}
              <div>
                <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center">
                  <div className="w-[5px] h-6 bg-purple-600 rounded mr-3"></div>
                  DQ Check Frequency
                </h3>
                {renderFrequencyDetails()}
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="text-gray-400 mb-4">
                <svg
                  className="mx-auto h-16 w-16"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                  />
                </svg>
              </div>
              <p className="text-gray-600 text-lg">
                No contract details available
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ContractDetailModal;
